import type { TranscriptSegment } from '@ai-meeting/shared'
import type { RealtimeToken } from '@soniox/client'

/** A token carrying the speaker name the panes label rows with. */
interface NamedRealtimeToken extends RealtimeToken {
  speaker_name?: string
}

/**
 * Turn a meeting's stored transcript back into tokens the live panes can render.
 *
 * Used to put a previous native session's transcript back on screen when a new
 * session starts on the same meeting: the bot leaves the moment the Teams panel
 * goes away, and the new session's socket only ever replays its own segments, so
 * without this the Input pane restarts empty (BRAI-447).
 *
 * `start_ms` is deliberately left off. These segments were timed against a
 * session that has ended, and the new session's own timeline restarts near zero
 * — carrying their old times over would make `sortBySpeechStart` interleave the
 * two halves, putting the new meeting's first words above the old ones. Leaving
 * it undefined is what that sort reads as "keep this where it is", so the
 * restored half stays above the live half in the order it was spoken. The cost
 * is that the restored rows carry no `mm:ss` marker, which is honest: their
 * clock is not the one the rest of the pane is counting on.
 *
 * @param segments Stored transcript, oldest first.
 * @returns One finalized source token per segment that has text.
 */
export function segmentsToTokens(segments: readonly TranscriptSegment[]): RealtimeToken[] {
  return segments
    .filter((segment) => segment.text?.trim())
    .map((segment): NamedRealtimeToken => {
      const speakerName = segment.speakerName?.trim() || segment.speaker?.trim()
      return {
        text: segment.text,
        confidence: 1,
        is_final: true,
        translation_status: 'original',
        speaker: segment.speakerId ?? segment.speaker,
        speaker_name: speakerName,
        language: segment.languageCode,
      } as NamedRealtimeToken
    })
}
